import { angularEvents } from "../bridge/angularEvents";
import { reactEvents } from "../bridge/reactEvents";
import { useEffect, useState, useRef } from "react";
import { Formik, Field, Form, FieldArray, useFormikContext, useField } from "formik";
import * as Yup from "yup";
import { MetaField } from "./MetaFields";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

export interface MetaFieldValue {
  id: number;
  name: string;
  articleId: number;
  metaFieldId: number;
  type: string;
  value: any;
  options: MetaFieldValueOption[];
}

export interface MetaFieldValueOption {
  id: number;
  value: string;
}

function DropdownField({
  options,
  name,
  isEditing,
}: {
  options: MetaFieldValueOption[];
  name: string;
  isEditing: boolean;
}) {
  return (
    <Field as="select" name={name} disabled={!isEditing}>
      {options.map((option) => (
        <option key={option.id} value={option.value}>
          {option.value}
        </option>
      ))}
    </Field>
  );
}

function formatDate(date: Date) {
  let year = date.getFullYear();
  let month = date.getMonth() + 1;
  let day = date.getDate();
  let formattedDate = `${year}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`;
  return formattedDate;
}

function DatepickerField({ name, isEditing }: { name: string; isEditing: boolean }) {
  const { setFieldValue } = useFormikContext<any>();
  const [field, meta] = useField(name);

  const handleDateChange = (date: Date | null) => {
    if (date) {
      let formattedDate = formatDate(date);
      setFieldValue(name, formattedDate);
    } else {
      setFieldValue(name, "");
    }
  };

  return (
    <div>
      <DatePicker
        dateFormat="yyyy-MM-dd"
        selected={field.value ? new Date(field.value) : null}
        onChange={handleDateChange}
        disabled={!isEditing}
      />
      {meta.touched && meta.error ? (
        <div className="error">{meta.error}</div>
      ) : null}
    </div>
  );
}

function MetaFieldValueComponent({
  field,
  isEditing,
  index,
  onChange
}: {
  field: MetaFieldValue;
  isEditing: boolean;
  index: number;
  onChange: (index: number, value: any) => void;
}) {
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    let value: any = e.target.value;
    if (field.type === "number") {
      value = value === "" ? "" : parseFloat(value);
    }
    onChange(index, value);
  };

  switch (field.type) {
    case "short text":
      return (
        <Field
          name={`metaFields.${index}.value`}
          type="text"
          readOnly={!isEditing}
          component="input"
          onChange={handleChange}
        />
      );
    case "long text":
      return (
        <Field
          name={`metaFields.${index}.value`}
          component="textarea"
          readOnly={!isEditing}
          onChange={handleChange}
        />
      );
    case "number":
      return (
        <Field
          name={`metaFields.${index}.value`}
          type="number"
          readOnly={!isEditing}
          component="input"
          onChange={handleChange}
        />
      );
    case "list":
      const newOption: MetaFieldValueOption = {
        id: 0,
        value: "",
      };

      const updatedOptions = field.options.concat(newOption);
      return (
        <DropdownField
          options={updatedOptions}
          name={`metaFields.${index}.value`}
          isEditing={isEditing}
        />
      );
    case "date":
      return (
        <DatepickerField
          name={`metaFields.${index}.value`}
          isEditing={isEditing}
        />
      );
    default:
      return <span>Unknown type</span>;
  }
}

function saveMetaFieldValues(values: MetaFieldValue[]) {
  const formattedValues = values.map(field => {
    if (field.type === "number") {
      field.value = field.value ?? "";
    }
    return field;
  });
  reactEvents.saveMetaFieldValues.dispatch(formattedValues);
}

export function MetaFieldValues({
  values,
  initialIsEditing,
}: {
  values: MetaFieldValue[];
  fields: MetaField[];
  initialIsEditing: boolean;
}) {
  const [isEditing, setIsEditing] = useState(initialIsEditing);

  const formValuesRef = useRef(values);

  useEffect(() => {
    const unsub = angularEvents.isEditing.subscribe((e: boolean) => {
      setIsEditing(e);
    });

    const unsubSaveArticle = angularEvents.saveArticle.subscribe(() => {
      saveMetaFieldValues(formValuesRef.current);
    });

    return () => {
      unsub();
      unsubSaveArticle();
    };
  }, [values]);

  useEffect(() => {
    console.log(`isEditing changed: ${isEditing}`);
  }, [isEditing]);

  const validationSchema = Yup.object().shape({
    metaFields: Yup.array().of(
      Yup.object().shape({
        name: Yup.string().required("Name is required"),
        type: Yup.string().required("Type is required"),
        value: Yup.mixed().required("Value is required"),
      })
    ),
  });

  const formattedValues = values.map(field => {
    if (field.type === "date") {
      field.value = field.value ? formatDate(new Date(field.value)) : "";
    }
    if (field.type === "number") {
      field.value = field.value ?? "";
    }
    return field;
  });

  return (
    <div>
      <h2>Meta field values</h2>
      <p>Set values specific to this article</p>
      <Formik
        initialValues={{ metaFields: formattedValues }}
        validationSchema={validationSchema}
        onSubmit={(formValues) => {
          saveMetaFieldValues(formValues.metaFields);
        }}
      >
        {({ values, handleSubmit, setFieldValue }) => {
          formValuesRef.current = values.metaFields;

          const handleFieldChange = (index: number, value: any) => {
            setFieldValue(`metaFields.${index}.value`, value);
            formValuesRef.current[index].value = value;
          };

          return (
            <Form onSubmit={handleSubmit}>
              <FieldArray name="metaFields">
                {() => (
                  <ul>
                    {values.metaFields.map((metaFieldValue, index) => (
                      <li
                        key={index}
                        style={{ listStyle: "none", marginBottom: "1rem" }}
                      >
                        <div>
                          <strong>{metaFieldValue.name}</strong>
                        </div>
                        <MetaFieldValueComponent
                          field={metaFieldValue}
                          isEditing={isEditing}
                          index={index}
                          onChange={handleFieldChange}
                        />
                      </li>
                    ))}
                  </ul>
                )}
              </FieldArray>
            </Form>
          );
        }}
      </Formik>
    </div>
  );
}
